import { useApp, useUserInfo } from "@next-platform/client-common-core";
import { Spinner } from "@nextui-org/react";
import React, { useEffect, useState } from "react";
import { Configure, ConfigureProps, InstantSearch } from "react-instantsearch";
import InstantSearchAdapter from "typesense-instantsearch-adapter";

import { createSearchClientConfig } from "@/components/search/utils/createSearchClientConfig";

import { SPICYCHAT_USER_PERMISSIONS } from "../../../constants/permissions";
import LeaderboardsTable from "./LeaderboardTable";
import { LeaderboardListingProps } from "./types";

export default function LeaderboardListing(props: LeaderboardListingProps) {
  const { selectedDate, setSelectedDate } = props;
  const { typesenseConfig: searchClientRemoteConfig } = useApp();
  const { permissions } = useUserInfo();

  const [instantSearchAdapter, setInstantSearchAdapter] = useState<null | InstantSearchAdapter>(null);
  const [activeTab, setActiveTab] = useState<React.Key>("New Bots");

  const filters = [`type:${activeTab}`];

  if ([...selectedDate].length) {
    filters.push(`date:${[...selectedDate][0]}`);
  }

  // Apply a filter based on the permissions to support the leaderboard approval workflow.
  //
  // - Public users: should see all published leaderboards.
  // - Admin users: do not get this filter applied, allowing them to see ALL records,
  //   including the new `pending` ones that are awaiting their review and approval.
  const isAdmin = permissions?.includes(SPICYCHAT_USER_PERMISSIONS.admin);

  if (!isAdmin) {
    filters.push("status:!=pending");
  }

  const allFilters = filters.join(" && ");

  const searchIndexName = `${searchClientRemoteConfig?.collectionNameLeaderboard}/sort/rank:asc`;

  const searchClientConfig = createSearchClientConfig({
    apiKey: searchClientRemoteConfig?.apiKeyLeaderboard,
    queryBy: "date",
    filterByOptions: { date: { exactMatch: true } },
  });

  const configProps = {
    hitsPerPage: 48,
    maxValuesPerFacet: 100,
    filters: allFilters,
  } as ConfigureProps;

  useEffect(() => {
    if (searchClientRemoteConfig?.apiKeyAllPublicCharacters) {
      const instantSearchAdapter = new InstantSearchAdapter(searchClientConfig);
      setInstantSearchAdapter(instantSearchAdapter);
    }
  }, [searchClientRemoteConfig?.apiKeyAllPublicCharacters]);

  if (!searchClientRemoteConfig?.apiKeyAllPublicCharacters || !instantSearchAdapter?.searchClient) {
    return <Spinner />;
  }

  return (
    <>
      <InstantSearch searchClient={instantSearchAdapter?.searchClient} indexName={searchIndexName}>
        <Configure {...configProps} />
        <LeaderboardsTable setActiveTab={setActiveTab} selectedDate={selectedDate} setSelectedDate={setSelectedDate} />
      </InstantSearch>
    </>
  );
}
